<script lang="ts">
  import { onMount, tick } from 'svelte';
  import type { Match } from '@multiview/shared';
  import { api, ApiError } from './lib/api/client.ts';
  import { remote } from './lib/input/remote.ts';
  import { focusFirst, move } from './lib/input/spatial.ts';
  import { engine } from './lib/player/engine.svelte.ts';
  import { isTv, platform } from './lib/platform.ts';
  import LoginView from './components/LoginView.svelte';
  import Mosaic from './components/Mosaic.svelte';
  import WatchOverlay from './components/WatchOverlay.svelte';
  import QuickBar from './components/QuickBar.svelte';
  import Hud from './components/Hud.svelte';
  import Ticker from './components/Ticker.svelte';
  import GoalBanner from './components/GoalBanner.svelte';
  import CardNotes from './components/CardNotes.svelte';
  import { ticker } from './lib/ticker.svelte.ts';
  import { jumpToBanner } from './lib/bannerJump.ts';
  import { applyPendingUpdate, initAppUpdates } from './lib/appUpdate.ts';
  import BrowseView from './components/BrowseView.svelte';
  import SetupView from './components/SetupView.svelte';
  import SettingsView from './components/SettingsView.svelte';
  import InfoPanel from './components/InfoPanel.svelte';

  type View = 'loading' | 'login' | 'setup' | 'browse' | 'settings' | 'watch';

  let view = $state<View>('loading');
  let firstRun = $state(false);
  let overlayOpen = $state(false);
  /** The control bar over the picture; the full menu is overlayOpen. */
  let quickOpen = $state(false);
  let hudOpen = $state(false);
  /** Side panel with match facts; `infoFocused` gives it the remote's arrows. */
  let infoOpen = $state(false);
  let infoFocused = $state(false);
  let startupError = $state<string | null>(null);

  // A timed switch announces itself shortly before, so it does not come as a jolt.
  let clock = $state(Date.now());
  $effect(() => {
    if (view !== 'watch' || engine.autoNextAt === null) return;
    const id = setInterval(() => (clock = Date.now()), 1000);
    return () => clearInterval(id);
  });
  const upcoming = $derived.by(() => {
    if (engine.autoNextAt === null) return null;
    const seconds = Math.round((engine.autoNextAt - clock) / 1000);
    if (seconds > 10 || seconds < 1) return null;
    return { label: engine.nextAutoAngle?.label ?? '', seconds };
  });

  /**
   * Three distinct states, deliberately not collapsed into one error: no account
   * exists yet, an account exists but this device is not signed in, or we are in.
   */
  async function bootstrap() {
    startupError = null;
    try {
      const auth = await api.authState();

      if (auth.needsSetup) {
        firstRun = true;
        view = 'login';
        return;
      }
      if (!auth.user) {
        firstRun = false;
        view = 'login';
        return;
      }

      // The provider limit shapes which layouts may be offered at all.
      const health = await api.health();
      engine.upstreamBudget = health.upstreamBudget;

      const sources = await api.listSources();
      view = sources.length === 0 ? 'setup' : 'browse';
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        firstRun = false;
        view = 'login';
        return;
      }
      startupError = err instanceof Error ? err.message : String(err);
      view = 'login';
    }
  }

  async function logout() {
    try {
      await api.logout();
    } catch {
      /* local state is cleared either way */
    }
    engine.teardown();
    view = 'login';
    firstRun = false;
  }

  function watchMatch(match: Match) {
    engine.loadMatch(match);
    ticker.open(match.id > 0 ? match.id : null, match.name);
    overlayOpen = false;
    quickOpen = false;
    view = 'watch';
  }

  function closeInfo() {
    infoOpen = false;
    infoFocused = false;
  }

  function openInfo() {
    overlayOpen = false;
    quickOpen = false;
    infoOpen = true;
    infoFocused = true;
  }

  function leaveWatch() {
    engine.teardown();
    ticker.close();
    overlayOpen = false;
    quickOpen = false;
    closeInfo();
    view = 'browse';
    applyPendingUpdate();
  }

  /**
   * Arrows mean different things depending on where you are: in watch mode they
   * cycle the main stream, everywhere else they move focus. Keeping that in one
   * place is the only way the remote stays predictable.
   */
  $effect(() => {
    remote.setMode(view === 'watch' && !overlayOpen && !quickOpen && !infoFocused ? 'watch' : 'browse');
  });

  // Entering a view or opening a panel has to land focus somewhere, or the first
  // D-pad press has nothing to move from.
  $effect(() => {
    const _view = view;
    const _overlay = overlayOpen;
    const _quick = quickOpen;
    const _info = infoFocused;
    void _view; void _overlay; void _quick; void _info;
    void tick().then(() => {
      if (view === 'watch' && !overlayOpen && !quickOpen && !infoFocused) {
        // Arrows go back to the mosaic; a focused panel button would keep them.
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        return;
      }
      // A touch screen has no use for a focus ring, and focusing the search
      // field there would pop up the keyboard on arrival.
      if (platform === 'mobile') return;
      const scope = document.querySelector<HTMLElement>('[data-focus-scope]') ?? document;
      focusFirst(scope);
    });
  });

  onMount(() => {
    void bootstrap();
    initAppUpdates(() => view === 'watch');

    /**
     * The Android wrapper asks before it closes the app: BACK belongs to the
     * page while there is an overlay to close or a watch view to leave, and to
     * the system once there is not.
     */
    window.__mvHandleBack = () => remote.dispatch({ type: 'back' });

    const unsubscribe = remote.subscribe((action) => {
      switch (action.type) {
        case 'nav': {
          const scope = document.querySelector<HTMLElement>('[data-focus-scope]') ?? document;
          return move(action.dir, scope);
        }

        case 'activate': {
          const active = document.activeElement;
          if (!(active instanceof HTMLElement)) return false;

          // Clicking a text field does nothing. Confirming inside one means
          // submitting its form -- the only way OK works on a remote, where
          // there is no way to reach the submit button except by navigating to
          // it first.
          if (active instanceof HTMLInputElement && active.form) {
            active.form.requestSubmit();
            return true;
          }

          active.click();
          return true;
        }

        case 'back':
          if (overlayOpen) { overlayOpen = false; return true; }
          if (quickOpen) { quickOpen = false; return true; }
          if (hudOpen) { hudOpen = false; return true; }
          if (infoOpen) { closeInfo(); return true; }
          if (view === 'watch') { leaveWatch(); return true; }
          if (view === 'settings') { view = 'browse'; return true; }
          return false;

        case 'overlay.toggle':
          if (view !== 'watch') return false;
          // A goal banner of another angle's match: OK jumps there.
          if (!overlayOpen && !quickOpen && jumpToBanner()) return true;
          // OK brings up the bar over the picture; its "Mehr" leads on to the menu.
          if (overlayOpen) overlayOpen = false;
          else quickOpen = !quickOpen;
          if (quickOpen) infoFocused = false;
          return true;

        case 'info.toggle':
          if (view !== 'watch') return false;
          // Closed -> open and operable; operable -> visible only, so the
          // arrows switch angles again; visible -> operable.
          if (!infoOpen) openInfo();
          else { infoFocused = !infoFocused; overlayOpen = false; quickOpen = false; }
          return true;

        case 'hud.toggle':
          hudOpen = !hudOpen;
          return true;

        case 'angle.cycle':
          if (view !== 'watch') return false;
          engine.cycleMain(action.delta);
          return true;

        case 'angle.select':
          if (view !== 'watch') return false;
          engine.selectMainByIndex(action.index);
          return true;

        case 'layout.cycle':
          if (view !== 'watch') return false;
          engine.cycleLayout(action.delta);
          return true;

        case 'audio.toggleMute':
          if (view !== 'watch') return false;
          engine.toggleMute();
          return true;

        case 'audio.pinFocused':
          if (view !== 'watch') return false;
          engine.pinAudioToFocused();
          return true;

        case 'sync.nudge':
          if (view !== 'watch') return false;
          engine.nudgeSync(action.deltaMs);
          return true;

        case 'playPause':
          if (view !== 'watch') return false;
          engine.togglePause();
          return true;
      }
      return false;
    });

    return () => {
      delete window.__mvHandleBack;
      unsubscribe();
    };
  });
</script>

<main class:tv={isTv}>
  {#if view === 'loading'}
    <div class="center boot" aria-busy="true">
      <h1>Multiview</h1>
      <span class="spinner"></span>
      <span class="faint">Verbinde mit dem Proxy …</span>
    </div>

  {:else if view === 'login'}
    <div data-focus-scope class="full">
      {#if startupError}
        <p class="startup-error">Proxy nicht erreichbar: {startupError}</p>
      {/if}
      <LoginView {firstRun} onDone={() => { void bootstrap(); }} />
    </div>

  {:else if view === 'setup'}
    <div data-focus-scope>
      <SetupView onDone={() => { void bootstrap(); }} onLogout={logout} />
    </div>

  {:else if view === 'browse'}
    <div data-focus-scope class="full">
      <BrowseView onWatch={watchMatch} onLogout={logout} onSettings={() => { view = 'settings'; }} />
    </div>

  {:else if view === 'settings'}
    <div data-focus-scope class="full">
      <SettingsView onClose={() => { view = 'browse'; }} />
    </div>

  {:else}
    <div class="watch">
      <div class="stage">
        <Mosaic showStats={hudOpen} />
        <Ticker />
        <GoalBanner />
        {#if !quickOpen && !overlayOpen}
          <CardNotes offsetEm={(ticker.visible.length > 0 ? 2.4 * ticker.style.size : 0) + (upcoming ? 2.2 : 0)} />
        {/if}

        {#if engine.paused}
          <div class="paused">Pause</div>
        {/if}
        {#if engine.globalMute}
          <div class="badge-mute">stumm</div>
        {/if}

        {#if hudOpen}
          <Hud />
        {/if}

        {#if quickOpen && !overlayOpen}
          <div data-focus-scope>
            <QuickBar
              onClose={() => { quickOpen = false; }}
              onMore={() => { quickOpen = false; overlayOpen = true; }}
              onInfo={openInfo}
            />
          </div>
        {:else if !overlayOpen}
          {#if upcoming}
            <div class="upcoming" class:below-ticker={ticker.visible.length > 0}>⇄ {upcoming.label} in {upcoming.seconds} s</div>
          {/if}
          {#if !isTv}
            <!-- Without a remote or keyboard there is no OK key, and the menu is
                 also the only way back to the overview. -->
            <button class="menu-button" type="button" onclick={() => { quickOpen = true; }}>☰ Steuerung</button>
          {/if}
          <div class="hint faint" class:top={platform === 'mobile'}>
            {#if platform === 'mobile'}
              Kachel antippen: Hauptbild · ☰ Steuerung
            {:else}
              ◀▶ Perspektive · ▲▼ Layout · OK Steuerung · i Spielinfos · Zurück Übersicht
            {/if}
          </div>
        {/if}
      </div>

      {#if infoOpen}
        <div class="info-host" data-focus-scope={infoFocused && !overlayOpen && !quickOpen ? true : undefined}>
          <InfoPanel focused={infoFocused} onClose={closeInfo} />
        </div>
      {/if}

      {#if overlayOpen}
        <div data-focus-scope class="overlay-host">
          <WatchOverlay onClose={() => { overlayOpen = false; }} onExit={leaveWatch} onInfo={openInfo} />
        </div>
      {/if}
    </div>
  {/if}
</main>

<style>
  main { height: 100%; position: relative; }
  .full { height: 100%; }
  .center { height: 100%; display: flex; align-items: center; justify-content: center; }
  .boot { flex-direction: column; gap: var(--gap); }
  .boot h1 { margin: 0; font-size: calc(var(--font-base) * 2); }
  .boot .spinner {
    width: 2em; height: 2em;
    border: 3px solid var(--border-strong);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .watch { height: 100%; position: relative; background: #000; display: flex; }
  .stage { flex: 1 1 auto; min-width: 0; height: 100%; position: relative; }
  /* Narrow enough to leave the mosaic the screen, wide enough for a table. */
  .info-host { flex: 0 0 clamp(20em, 28vw, 30em); height: 100%; min-width: 0; }
  :global(main.tv) .info-host { flex-basis: clamp(18em, 30vw, 26em); }
  .overlay-host { position: absolute; inset: 0; z-index: 30; }

  .upcoming.below-ticker { top: calc(var(--safe-inset) + 2.4em); }

  .upcoming {
    position: absolute;
    top: var(--safe-inset);
    right: var(--safe-inset);
    z-index: 15;
    padding: 0.3em 0.8em;
    font-size: var(--font-sm);
    background: rgba(0, 0, 0, 0.75);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius);
    pointer-events: none;
  }

  .startup-error {
    margin: 0;
    padding: var(--gap) var(--safe-inset);
    background: #3a1414;
    color: #ffb4b4;
    font-size: var(--font-sm);
  }

  .paused, .badge-mute {
    position: absolute;
    top: var(--safe-inset);
    left: 50%;
    transform: translateX(-50%);
    padding: 0.3em 0.9em;
    background: rgba(0, 0, 0, 0.8);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius);
    font-weight: 600;
    z-index: 15;
  }

  .badge-mute { top: auto; bottom: var(--safe-inset); }

  /* Sits over the mosaic rather than taking layout space, so it never shrinks a
     tile; it fades out of the way on a TV where it would just be clutter. */
  .hint {
    position: absolute;
    bottom: calc(var(--safe-inset) / 2);
    left: 50%;
    transform: translateX(-50%);
    font-size: var(--font-sm);
    background: rgba(0, 0, 0, 0.6);
    padding: 0.25em 0.8em;
    border-radius: var(--radius);
    max-width: calc(100% - 2 * var(--safe-inset));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
    animation: fade-out 6s 4s forwards;
  }

  /* On a phone the bottom edge belongs to the menu button. */
  .hint.top { bottom: auto; top: calc(var(--safe-inset) / 2); }

  /* The bottom-right corner is the one place no tile badge ever sits. */
  .menu-button {
    position: absolute;
    bottom: calc(var(--safe-inset) + 6px);
    right: calc(var(--safe-inset) + 6px);
    z-index: 16;
    padding: 0.35em 0.75em;
    font-size: var(--font-sm);
    background: rgba(0, 0, 0, 0.6);
    border-color: var(--border-strong);
    opacity: 0.75;
  }

  .menu-button:hover { opacity: 1; }

  @keyframes fade-out { to { opacity: 0; } }
</style>
